import { UploadService } from "@/services/APIs";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd/es/upload";
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";

export const CustomFileUpload = (
  props: UploadProps & {
    action: string;
    value?: File[] | string[];
    onChange?: (value: File[] | string[]) => void;
    defaultFileList?: File[];
    multiple?: boolean;
  }
) => {
  const { id } = useParams();
  const editMode = !!id;
  const { value = [], onChange, multiple = false, ...restProps } = props;
  // const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
  // const editMode = !!id;

  const [uploadedFiles, setUploadedFiles] = useState<any>([]);
  const previousParsedValueRef = useRef<string>("");

  useEffect(() => {
    if (!editMode && !value) {
      return;
    }

    try {
      let parsedValue = value;

      if (typeof value === "string") {
        parsedValue = JSON.parse(value);
      }

      if (!Array.isArray(parsedValue)) {
        parsedValue = [parsedValue];
      }

      // Only update if the parsed value actually changed
      const parsedValueStr = JSON.stringify(parsedValue);

      if (parsedValueStr !== previousParsedValueRef.current) {
        setUploadedFiles(parsedValue);
        previousParsedValueRef.current = parsedValueStr;
      }
    } catch (e) {
      console.error("Error parsing uploaded files:", e);
      const emptyStr = JSON.stringify([]);
      if (previousParsedValueRef.current !== emptyStr) {
        setUploadedFiles([]);
        previousParsedValueRef.current = emptyStr;
      }
    }
  }, [editMode, value]);

  const handleFileSelect = async (file: File) => {
    // Upload only the new file, not all previously selected files
    try {
      const response = await UploadService.uploadFiles({
        folderName: "uploads",
        files: [file], // Upload only the new file
      });

      if (response && Array.isArray(response)) {
        const uploadedUrls = response;
        const currentUploaded = Array.isArray(uploadedFiles)
          ? uploadedFiles
          : [];

        // Get existing URLs to avoid duplicates
        const existingUrls = currentUploaded.map((f: any) => {
          if (typeof f === "string") {
            return f;
          }
          return f?.file_path ?? f?.url ?? f?.name ?? "";
        });

        // Only add new URLs that aren't already in the list
        const newUrls = uploadedUrls.filter(
          (url: string) => !existingUrls.includes(url)
        );

        // Combine existing files with new URLs
        const newValue = [...currentUploaded, ...newUrls];

        onChange?.(newValue);
        setUploadedFiles(newValue);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
    return false;
  };

  const handleRemove = (file: any) => {
    const fileToRemove = file.file_path ?? file.url ?? file.name;
    const filteredFiles = uploadedFiles.filter((f: any) => {
      const fUrl =
        typeof f === "string" ? f : (f?.file_path ?? f?.url ?? f?.name ?? "");
      return fUrl !== fileToRemove;
    });

    setUploadedFiles(filteredFiles);
    onChange?.(filteredFiles);
  };

  const handleChange = (info: any) => {
    if (info.file.status === "removed") {
      handleRemove(info.file);
    }
  };

  const handlePreview = (file: any) => {
    let url = file.url ?? file.file_path;

    if (!url) {
      return;
    }

    // 🔒 Force HTTPS
    if (url.startsWith("http://djangoproject.universal-software.in")) {
      url = url.replace("http://", "https://");
    }

    window.open(url, "_blank");
  };

  const fileList: UploadFile[] =
    uploadedFiles?.map((file: any, index: number) => {
      // Handle string URLs
      if (typeof file === "string") {
        const filename = file.split("/").pop() ?? "file";
        return {
          uid: `rc-upload-${index}`,
          name: filename,
          status: "done",
          url: file,
        };
      }

      // Handle file objects
      if (
        typeof file === "object" &&
        (file?.file_path || file?.file_original_name || file?.url)
      ) {
        return {
          uid: file?.id ?? `rc-upload-${index}`,
          name: file?.file_original_name ?? file?.name ?? "file",
          status: "done",
          url: file?.file_path ?? file?.url,
        };
      }

      return {
        uid: `rc-upload-${index}`,
        name: "No file",
        status: "done",
        url: "",
      };
    }) ?? [];

  const showUploadButton =
    multiple ||
    !uploadedFiles ||
    uploadedFiles.length === 0 ||
    uploadedFiles.every((f: any) => f === null);

  return (
    <div className="space-y-2">
      <Upload
        {...restProps}
        beforeUpload={handleFileSelect}
        onChange={handleChange}
        onRemove={handleRemove}
        maxCount={multiple ? undefined : 1}
        multiple={multiple}
        fileList={fileList}
        showUploadList={{
          showRemoveIcon: true,
          showDownloadIcon: true,
          showPreviewIcon: true,
          downloadIcon: <DownloadOutlined />,
          removeIcon: <DeleteOutlined />,
          previewIcon: <EyeOutlined />,
        }}
        onPreview={handlePreview} // 👈 handle click event on file name or icon
      >
        {showUploadButton && (
          <Button icon={<UploadOutlined />}>
            {multiple ? "Upload Files" : "Upload File"}
          </Button>
        )}
      </Upload>
    </div>
  );
};
